import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  Chip,
} from '@mui/material';
import {
  ScreenWrap,
  SearchInput,
  CheckboxInput,
  HeaderBreadcrumbs,
  SoloLoading,
  ScreenError,
} from '.';
import EditContentTagsModal from './modals/EditContentTagsModal';
import { PencilSimple } from '@phosphor-icons/react';
import { useParams } from 'react-router';
import { useTypedSelector } from '../reducers';
import useModifiableParams from '../hooks/use-modifiable-params';
import { Api } from '../api';
import { format, parseISO } from 'date-fns';
import { useUserStatusContext } from '../contexts';

interface ContentViewsTableProps {
  userId: string;
  params: any;
  tableSortLabelProps: any;
  tablePaginationProps: any;
}

function ContentViewsTable({
  userId,
  params,
  tableSortLabelProps,
  tablePaginationProps,
}: ContentViewsTableProps) {
  const queryParams: any = {
    'exists[libraryContent]': true,
  };

  if (params['libraryContent.title']) {
    queryParams['libraryContent.title'] = params['libraryContent.title'];
  }

  if (params.answersOnly === 'true') {
    queryParams.answersOnly = 'true';
  }

  if (params.orderBy === 'viewedAt') {
    queryParams['order[viewedAt]'] =
      params.orderDirection === 'asc' ? 'asc' : 'desc';
  } else {
    queryParams['order[viewedAt]'] = 'desc';
  }

  const api = Api.UserContentView.useReadCollectionLibraryContentViews(
    userId,
    queryParams,
  );

  if (api.error) return <ScreenError {...api} />;

  const contentViews = api.data?.member;

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>
              <TableSortLabel {...tableSortLabelProps('viewedAt')}>
                Viewed at
              </TableSortLabel>
            </TableCell>
            <TableCell>Question</TableCell>
            <TableCell>User Reply</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {contentViews ? (
            contentViews.map((item) => (
              <TableRow key={item['@id']}>
                <TableCell>
                  <Typography variant="body2">
                    {item.libraryContent?.title || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {item.viewedAt
                      ? format(parseISO(item.viewedAt), 'd MMM yyyy, HH:mm')
                      : '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {item.libraryContent?.question || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ wordBreak: 'break-word', maxWidth: 300 }}
                  >
                    {item.libraryContent?.userAnswer?.answer || '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <SoloLoading />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter
          sx={{
            borderTop: '1px solid rgba(224, 224, 224, 1)',
          }}
        >
          <TableRow>
            <TablePagination
              {...tablePaginationProps({
                totalItems: api.data?.totalItems ?? 0,
                rowsPerPage: 10,
              })}
              count={api.data?.totalItems ?? 0}
              colSpan={4}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}

export default function ContentInfo() {
  const { hasEditAccess } = useUserStatusContext();
  const { id } = useParams();
  const { viewMode } = useTypedSelector((state) => state.auth);

  const statsApi = Api.UserStats.useReadItemStats(id || '');

  const [editTagsModalOpen, setEditTagsModalOpen] = React.useState(false);

  const {
    getParams,
    setModifiedParams,
    tableSortLabelProps,
    tablePaginationProps,
  } = useModifiableParams({
    defaults: { page: '1' },
    defaultOrder: ['viewedAt', 'desc'],
  });

  const params = getParams();

  if (statsApi.error) return <ScreenError {...statsApi} />;

  if (!statsApi.data) {
    return (
      <ScreenWrap>
        <SoloLoading />
      </ScreenWrap>
    );
  }

  const isAdmin = viewMode === 'admin';
  const user = statsApi.data.user;

  const getDisplayTags = () => {
    const tags = statsApi.data?.user.contentTags ?? [];
    if (!tags.length) return [] as string[];
    return tags.map((t) => t.userFacingName).filter(Boolean) as string[];
  };

  return (
    <ScreenWrap>
      <Stack spacing={3}>
        <Box>
          <HeaderBreadcrumbs
            title="Content Info"
            items={[
              {
                name: isAdmin ? 'App Users' : 'Your Clients',
                url: '/users',
              },
              {
                name: user.name || 'User',
                url: `/users/${id}/user-dashboard`,
              },
              {
                name: 'Content Info',
              },
            ]}
          />
        </Box>

        <Card>
          <CardHeader
            title={
              <Typography variant="h6">Viewed Content & Questions</Typography>
            }
          />
          <CardContent>
            <Stack spacing={3}>
              <Stack
                direction="row"
                alignItems="end"
                spacing={2}
                sx={{ width: '100%' }}
              >
                <Box sx={{ flex: 1 }}>
                  <SearchInput
                    label="Search"
                    value={params['libraryContent.title'] || ''}
                    onChange={(e) =>
                      setModifiedParams({
                        'libraryContent.title': e.target.value,
                        page: '1',
                      })
                    }
                    placeholder="Search by title"
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: 56,
                    minWidth: 250,
                  }}
                >
                  <CheckboxInput
                    label=""
                    checkboxLabel="Only show items with user replies"
                    labelId="user-replies"
                    checked={params.answersOnly === 'true'}
                    onChange={(checked) =>
                      setModifiedParams({
                        answersOnly: checked ? 'true' : '',
                        page: '1',
                      })
                    }
                  />
                </Box>
              </Stack>

              <ContentViewsTable
                userId={id || ''}
                params={params}
                tableSortLabelProps={tableSortLabelProps}
                tablePaginationProps={tablePaginationProps}
              />
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title="Content Tags"
            subheader="We give content recommendations to the user based on these tags"
            action={
              hasEditAccess ? (
                <Button
                  variant="text"
                  startIcon={<PencilSimple />}
                  onClick={() => setEditTagsModalOpen(true)}
                >
                  Edit
                </Button>
              ) : null
            }
          />
          <CardContent>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {getDisplayTags().map((tag, index) => (
                  <Chip key={`${tag}-${index}`} label={tag} color="default" />
                ))}
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <EditContentTagsModal
          open={editTagsModalOpen}
          onClose={() => setEditTagsModalOpen(false)}
          userId={id}
        />
      </Stack>
    </ScreenWrap>
  );
}
